/**
 * useListPagination.ts — the row window for an Admin list.
 *
 * By default a list shows EVERY row and its footer shows only the count, as
 * User Groups, Discounts and Promo Codes always have (the API returns every
 * record; there is nothing to page). Pass `paged: true` only for a list that
 * already paged before (Lead Sources, Lead Fields): 25 rows a page, 10 / 25 /
 * 50 / 100 to choose from, and back to page 1 whenever the search or a filter
 * changes (pass those values as `resetOn`).
 *
 * The list is already filtered and sorted by the page; this only slices it.
 * The page number never runs past the last page — deleting the last row on
 * the last page shows the page before it instead of an empty one.
 *
 * Pair with PaginationFooter (AdminList.tsx). Not the server-paging
 * hooks/usePagination, which tracks an offset for API calls.
 */
import { useCallback, useMemo, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

/** What PaginationFooter needs. */
export interface ListPaginationState {
  /** False: every row is shown and the footer shows only the count. */
  paged: boolean;
  /** 1-based, never past totalPages. */
  page: number;
  pageSize: number;
  /** The page size the list opened with (the footer drops its controls while that shows every row). */
  initialPageSize: number;
  /** Rows across every page. */
  total: number;
  /** At least 1, even for an empty list. */
  totalPages: number;
  /** 1-based index of the first row shown, 0 when the list is empty. */
  from: number;
  /** 1-based index of the last row shown, 0 when the list is empty. */
  to: number;
  setPage: (page: number) => void;
  /** Also goes back to page 1. */
  setPageSize: (size: number) => void;
}

export interface ListPagination<T> extends ListPaginationState {
  /** The rows on the current page. */
  pageItems: T[];
}

export interface UseListPaginationOptions {
  /** Page the rows (Lead Sources / Lead Fields). Default false: show them all. */
  paged?: boolean;
  initialPageSize?: number;
  /**
   * Values that, when any of them changes, send the list back to page 1 —
   * the search text, Show deleted, each filter's selection.
   */
  resetOn?: readonly unknown[];
}

export function useListPagination<T>(
  items: T[],
  { paged = false, initialPageSize = DEFAULT_PAGE_SIZE, resetOn = [] }: UseListPaginationOptions = {},
): ListPagination<T> {
  const [requestedPage, setRequestedPage] = useState(1);
  const [chosenPageSize, setPageSizeState] = useState(initialPageSize);

  // Back to page 1 the moment the search or a filter changes — in this
  // render, not an effect later, so the old page number never shows.
  const resetKey = JSON.stringify(resetOn);
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setRequestedPage(1);
  }

  const total = items.length;
  // Unpaged: one page holding every row.
  const pageSize = paged ? chosenPageSize : Math.max(1, total);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, requestedPage), totalPages);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);

  const setPage = useCallback((next: number) => setRequestedPage(Math.max(1, next)), []);
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setRequestedPage(1);
  }, []);

  return { paged, page, pageSize, initialPageSize, total, totalPages, from, to, pageItems, setPage, setPageSize };
}
